import { useState, useEffect, useRef, createContext } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  User as UserIcon,
  Bell,
} from "lucide-react";
import { useAuthState, useLogout } from "react-redux-django-auth";
import UniversalNotificationDropdown from "../../components/notifications/UniversalNotificationDropdown";
import Brand from "../../components/common/Brand";

export const SidebarContext = createContext({ isExpanded: false });

const DashboardLayout = ({
  sidebarNavContent,
  topBarAddons = null,
  footerComponent = null,
  children,
}: any) => {
  const { user: rawUser } = useAuthState();
  const { logout } = useLogout();

  // Safely extract user profile (handles both flat and nested payload: { user: { ... }, access, refresh })
  const user =
    (rawUser as any)?.user || (rawUser as any)?.data?.user || rawUser;

  // Dynamically resolve user details
  const resolvedName =
    user?.first_name || user?.last_name
      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
      : user?.email || "Guest User";

  const formatRole = (r?: string) => {
    if (!r) return "";
    const lower = r.toLowerCase();
    if (lower === "superadmin") return "Super Administrator";
    if (lower === "admin") return "Administrator";
    if (lower === "instructor") return "Instructor";
    if (lower === "student") return "Student";
    return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
  };

  const resolvedRole =
    formatRole(user?.role) ||
    (user?.is_superuser
      ? "Super Administrator"
      : user?.is_staff
        ? "Administrator"
        : "Student");

  // Validate avatar (rejects broken Cloudinary '.../None' or null paths)
  const isValidPhoto = (url?: string | null) =>
    Boolean(
      url &&
      typeof url === "string" &&
      !url.includes("/None") &&
      !url.endsWith("null"),
    );

  const resolvedPic =
    (isValidPhoto(user?.photo_url) ? user.photo_url : null) ||
    (isValidPhoto(user?.get_photo_url) ? user.get_photo_url : null) ||
    (isValidPhoto(user?.profile_picture) ? user.profile_picture : null) ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(resolvedName)}&background=3e57da&color=fff&bold=true`;

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error: any) {
      console.error("Sign out error:", error);
    } finally {
      window.location.href = "/login";
    }
  };

  const location = useLocation();

  const getProfilePath = () => {
    if (location.pathname.startsWith("/superadmin/dashboard"))
      return "/superadmin/dashboard/profile";
    if (location.pathname.startsWith("/staff/dashboard"))
      return "/staff/dashboard/profile";
    if (location.pathname.startsWith("/instructor/dashboard"))
      return "/instructor/dashboard/profile";
    if (location.pathname.startsWith("/student/dashboard"))
      return "/student/dashboard/profile";

    const r = (user?.role || "").toUpperCase();
    if (user?.is_superuser || r === "SUPERADMIN")
      return "/superadmin/dashboard/profile";
    if (user?.is_staff || r === "ADMIN") return "/staff/dashboard/profile";
    if (r === "INSTRUCTOR") return "/instructor/dashboard/profile";
    return "/student/dashboard/profile";
  };

  const getNotificationPath = () => {
    if (location.pathname.startsWith("/superadmin/dashboard"))
      return "/superadmin/dashboard/notifications";
    if (location.pathname.startsWith("/staff/dashboard"))
      return "/staff/dashboard/notifications";
    if (location.pathname.startsWith("/instructor/dashboard"))
      return "/instructor/dashboard/notifications";
    if (location.pathname.startsWith("/student/dashboard"))
      return "/student/dashboard/notifications";

    const r = (user?.role || "").toUpperCase();
    if (user?.is_superuser || r === "SUPERADMIN")
      return "/superadmin/dashboard/notifications";
    if (user?.is_staff || r === "ADMIN")
      return "/staff/dashboard/notifications";
    if (r === "INSTRUCTOR") return "/instructor/dashboard/notifications";
    return "/student/dashboard/notifications";
  };

  const [scrolled, setScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const isExpanded = isHovered || isPinned;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY >= 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.matchMedia("(min-width: 1024px)").matches) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div
      className="cht-flex cht-h-screen cht-bg-[#f2f4fd] cht-overflow-hidden"
      style={{ fontFamily: "'Raleway', sans-serif" }}
    >
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="cht-fixed cht-inset-0 cht-z-20 cht-bg-black/20 cht-backdrop-blur-sm lg:cht-hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* ── SIDEBAR ── */}
      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: isExpanded ? "256px" : "68px",
          transition: "width 0.3s cubic-bezier(0.175,0.885,0.32,1.1)",
        }}
        className={[
          "cht-fixed lg:cht-relative cht-inset-y-0 cht-left-0 cht-z-30 cht-flex cht-flex-col",
          "cht-bg-white cht-border-r cht-border-gray-100",
          "cht-shadow-[2px_0_24px_rgba(0,0,0,0.04)] cht-overflow-hidden",
          isMobileOpen
            ? "cht-translate-x-0"
            : "-cht-translate-x-full lg:cht-translate-x-0",
          "cht-transition-transform cht-duration-300 lg:cht-transition-none",
        ].join(" ")}
      >
        <SidebarContext.Provider value={{ isExpanded }}>
          {/* Brand */}
          <div className="cht-flex cht-items-center cht-justify-between cht-h-16 cht-px-[18px] cht-border-b-2 cht-border-[#3e57da]/20 cht-flex-shrink-0">
            <Brand isExpanded={isExpanded} size="sm" to="/dashboard" />

            {/* Pin toggle */}
            <div
              onClick={() => setIsPinned(!isPinned)}
              style={{
                opacity: isExpanded ? 1 : 0,
                transition: "opacity 0.2s ease",
              }}
              className="cht-flex-shrink-0 cht-w-6 cht-h-6 cht-rounded-full cht-bg-gray-100 hover:cht-bg-[#3e57da]/10 hover:cht-text-[#3e57da] cht-text-gray-400 cht-flex cht-items-center cht-justify-center cht-transition-colors cht-cursor-pointer"
            >
              {isPinned ? (
                <ChevronLeft size={17} />
              ) : (
                <ChevronRight size={17} />
              )}
            </div>
          </div>

          {/* Nav */}
          <nav className="cht-flex-1 cht-overflow-y-auto cht-overflow-x-hidden cht-py-3 cht-px-2 cht-space-y-0.5">
            {sidebarNavContent}
          </nav>

          {/* User footer */}
          <div className="cht-flex-shrink-0 cht-border-t cht-border-gray-100 cht-p-2 cht-flex cht-items-center cht-gap-3">
            <img
              src={resolvedPic}
              alt={resolvedName}
              className="cht-flex-shrink-0 cht-w-8 cht-h-8 cht-rounded-full cht-object-cover cht-ring-1 cht-ring-black/[0.08]"
            />
            <div
              style={{
                opacity: isExpanded ? 1 : 0,
                width: isExpanded ? "auto" : 0,
                overflow: "hidden",
                transition: "opacity 0.25s ease, width 0.3s ease",
                flex: 1,
                minWidth: 0,
              }}
            >
              <p className="cht-mb-0 cht-text-[12px] cht-font-semibold cht-text-[#1e293b] cht-truncate cht-whitespace-nowrap">
                {resolvedName}
              </p>
              <p className="cht-mb-0 cht-text-[10px] cht-text-gray-400 cht-whitespace-nowrap">
                {resolvedRole}
              </p>
            </div>
            <div
              onClick={handleSignOut}
              style={{
                opacity: isExpanded ? 1 : 0,
                transition: "opacity 0.2s ease",
              }}
              className="cht-flex-shrink-0 cht-p-1.5 cht-px-3 cht-rounded-full hover:cht-bg-red-50 hover:cht-text-red-500 cht-text-gray-400 cht-transition-all active:cht-scale-95 cht-outline-none cht-focus:ring-2 cht-focus:ring-red-500/20 cht-cursor-pointer"
            >
              <LogOut size={16} />
            </div>
          </div>
        </SidebarContext.Provider>
      </aside>

      {/* ── MAIN ── */}
      <div className="cht-flex cht-flex-col cht-flex-1 cht-min-w-0 cht-overflow-y-auto">
        {/* Topbar */}
        <header
          className={[
            "cht-sticky cht-top-0 cht-z-[100] cht-flex cht-items-center cht-justify-between cht-px-4 md:cht-px-6 cht-py-3",
            "cht-bg-white/75 cht-backdrop-blur-xl cht-border-b cht-border-gray-200/60",
            "cht-transition-shadow cht-duration-300",
            scrolled
              ? "cht-shadow-[0_2px_20px_rgba(0,0,0,0.06)]"
              : "cht-shadow-none",
          ].join(" ")}
        >
          {/* Left */}
          <div className="cht-flex cht-items-center cht-gap-3">
            <div
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="lg:cht-hidden cht-p-2 cht-rounded-[10px] cht-text-gray-500 hover:cht-bg-gray-100 hover:cht-text-[#3e57da] cht-transition-all cht-cursor-pointer"
            >
              <Menu size={20} />
            </div>
          </div>

          {/* Right */}
          <div className="cht-flex cht-items-center cht-gap-2">
            {topBarAddons}

            <UniversalNotificationDropdown />

            {/* Avatar dropdown */}
            <div className="cht-relative" ref={userMenuRef}>
              <div
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="cht-flex cht-items-center cht-gap-2 cht-pl-2 cht-pr-3 cht-py-1.5 cht-rounded-full hover:cht-bg-gray-100 cht-transition-all active:cht-scale-95 cht-outline-none cht-focus:ring-2 cht-focus:ring-[#3e57da]/10 cht-cursor-pointer"
              >
                <img
                  src={resolvedPic}
                  alt={resolvedName}
                  className="cht-w-8 cht-h-8 cht-rounded-full cht-object-cover cht-ring-2 cht-ring-[#3e57da]/20"
                />
                <span className="cht-hidden sm:cht-block cht-text-sm cht-font-medium cht-text-[#1e293b] cht-max-w-[120px] cht-truncate">
                  {resolvedName}
                </span>
                <ChevronDown
                  size={14}
                  className={`cht-text-gray-400 cht-transition-transform cht-duration-200 ${userMenuOpen ? "cht-rotate-180" : ""}`}
                />
              </div>

              {userMenuOpen && (
                <div
                  className="cht-absolute cht-right-0 cht-mt-2 cht-w-52 cht-bg-white/90 cht-backdrop-blur-xl cht-border cht-border-gray-200/60 cht-rounded-[18px] cht-shadow-[0_10px_40px_rgba(0,0,0,0.10)] cht-py-2 cht-z-50"
                  style={{ animation: "fadeInDown 0.15s ease-out" }}
                >
                  <div className="cht-px-4 cht-py-3 cht-border-b cht-border-gray-100">
                    <p className="cht-mb-0 cht-text-sm cht-font-semibold cht-text-[#1e293b] cht-truncate">
                      {resolvedName}
                    </p>
                    <p className="cht-mb-0 cht-text-xs cht-text-gray-400 cht-mt-0.5">
                      {resolvedRole}
                    </p>
                  </div>
                  <div className="cht-py-1">
                    <Link
                      to={getProfilePath()}
                      onClick={() => setUserMenuOpen(false)}
                      className="cht-flex cht-items-center cht-gap-3 cht-w-full cht-px-4 cht-py-2.5 cht-text-sm cht-font-medium cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] cht-transition-colors cht-no-underline"
                    >
                      <UserIcon size={15} /> Profile & Settings
                    </Link>
                    <Link
                      to={getNotificationPath()}
                      onClick={() => setUserMenuOpen(false)}
                      className="cht-flex cht-items-center cht-gap-3 cht-w-full cht-px-4 cht-py-2.5 cht-text-sm cht-font-medium cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] cht-transition-colors cht-no-underline"
                    >
                      <Bell size={15} /> Notifications
                    </Link>
                  </div>
                  <div className="cht-border-t cht-border-gray-100">
                    <div
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleSignOut();
                      }}
                      className="cht-flex cht-items-center cht-gap-3 cht-w-full cht-px-4 cht-py-2.5 cht-text-sm cht-font-medium cht-text-red-500 hover:cht-bg-red-50 cht-transition-colors cht-rounded-b-[16px] cht-cursor-pointer"
                    >
                      <LogOut size={15} /> Sign Out
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="cht-flex-1 cht-p-6">{children}</main>

        {footerComponent && (
          <footer className="cht-border-t cht-border-gray-100">
            {footerComponent}
          </footer>
        )}
      </div>
    </div>
  );
};

export default DashboardLayout;
